import { isDaycareOpenOnMoment } from '@/modules/helper/daycareHelper'
import { Daycare, ExpirationDuration } from '@/types/Daycare'
import { OccasionalSlotIcon } from '@/types/Slots'
import moment, { Moment } from 'moment-timezone'
import { DAY_FORMAT, DAYTIME_FORMAT, Expiry, HOUR_MINUTE_FORMAT, INVALID_DATES_ERROR } from './constants'

export interface OccasionalSlotProps {
    date: string
    start: string
    end: string
    childId: number | undefined
    sectionId: number
    childrenPriority: number[]
    groupPriority: number[]
    scheduled: string | undefined
    expiryChoice: Expiry
    internalNote: string | undefined
    externalNote: string | undefined
    externalIcon: OccasionalSlotIcon | undefined
    placeId: number | undefined
}

export class OccasionalSlot {
    date!: string
    start!: string
    end!: string
    childId: number | undefined
    sectionId!: number
    childrenPriority: number[] = []
    groupPriority: number[] = []
    scheduled: string | undefined
    expiry: string | undefined
    internalNote: string | undefined
    externalNote: string | undefined
    externalIcon: OccasionalSlotIcon | undefined
    placeId: number | undefined

    constructor(daycare: Daycare, props: OccasionalSlotProps, isScheduled: boolean = false) {
        const { expiryChoice, externalIcon, ...rest } = props

        Object.assign(this, rest)

        this.verifyDateValidity(daycare)
        this.setExternalIcon(externalIcon)
        this.setExpirance(expiryChoice)
        this.setScheduled(isScheduled)
    }

    verifyDateValidity(daycare: Daycare): void {
        const { date, start, end } = this
        const startTime: Moment = moment(start, HOUR_MINUTE_FORMAT)
        const endTime: Moment = moment(end, HOUR_MINUTE_FORMAT)

        const fullStartDate: Moment = moment(date, DAYTIME_FORMAT).set({
            hour: startTime.hour(),
            minute: startTime.minute()
        })
        const fullEndDate: Moment = moment(date, DAYTIME_FORMAT).set({ hour: endTime.hour(), minute: endTime.minute() })

        if (
            isDaycareOpenOnMoment(daycare, { moment: fullStartDate }) &&
            isDaycareOpenOnMoment(daycare, { moment: fullEndDate }) &&
            startTime <= endTime
        ) {
            return
        } else {
            throw new Error(INVALID_DATES_ERROR)
        }
    }

    setConfirmedChild({ childId }: Pick<OccasionalSlotProps, 'childId'>) {
        this.childId = childId
        this.setExpirance({ type: 'date', expiryDate: this.expiry })
    }

    setExternalIcon(externalIcon: OccasionalSlotIcon | undefined): void {
        this.externalIcon = externalIcon === OccasionalSlotIcon.None ? undefined : externalIcon
    }

    setExpirance(expiryDateProp: Expiry): void {
        const { date, start } = this
        if (this.hasChild()) {
            this.expiry = undefined
            return
        }
        if ('expiryDate' in expiryDateProp) {
            const { expiryDate } = expiryDateProp
            this.expiry = expiryDate
            return
        } else if ('expiryDuration' in expiryDateProp) {
            const { expiryDuration } = expiryDateProp
            if (expiryDuration === ExpirationDuration.expiration0 || expiryDuration === undefined) {
                this.expiry = undefined
                return
            }
            this.expiry = moment(date + ' ' + start, DAYTIME_FORMAT)
                .subtract(Number(expiryDuration.split('_')[1]), 'hour')
                .format(DAYTIME_FORMAT)
            return
        }
    }

    hasChild(): boolean {
        return this.childId !== undefined
    }

    setScheduled(isScheduled?: boolean): void {
        if (isScheduled === false || this.hasChild()) {
            this.scheduled = undefined
            return
        }

        const { end, date } = this
        const endTime: Moment = moment(end, HOUR_MINUTE_FORMAT)
        const fullEndTime: Moment = moment(date, DAY_FORMAT).set({
            hour: endTime.hour(),
            minute: endTime.minute()
        })
        const isScheduledBeforeEndDate: boolean = moment(this.scheduled, DAYTIME_FORMAT).isSameOrBefore(fullEndTime)
        if (this.scheduled !== undefined && isScheduledBeforeEndDate  && this.scheduled !== '') { 
            return
        } else {
        this.scheduled = moment(fullEndTime).format(DAYTIME_FORMAT)
        }
    }

    static isSlotValid(daycare: Daycare, props: OccasionalSlotProps): boolean {
        try {
            new OccasionalSlot(daycare, props)
            return true
        } catch {
            return false
        }
    }
}
